'use client'

// Application metadata
export const APP_CONFIG = {
  name: 'ETH Pass',
  fullName: 'Ethiopian Digital Identity System',
  organization: 'Federal Democratic Republic of Ethiopia',
  department: 'Federal IT Security Department',
  version: '1.0.0',
  supportEmail: 'support@ethpass.gov.et',
  supportPhone: '+251 11 557 8899',
} as const

// User roles
export const USER_ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  STAFF: 'staff',
  CITIZEN: 'citizen',
} as const

// Authentication constants
export const AUTH_CONFIG = {
  SESSION_TIMEOUT: 30 * 60 * 1000, // 30 minutes
  WARNING_TIME: 5 * 60 * 1000, // 5 minutes before timeout
  MAX_LOGIN_ATTEMPTS: 5,
  LOCKOUT_DURATION: 30 * 60 * 1000, // 30 minutes
  PASSWORD_MIN_LENGTH: 12,
  OTP_LENGTH: 6,
  OTP_EXPIRY_MINUTES: 5, // OTP validity window
  OTP_RESEND_COOLDOWN_SECONDS: 60, // Cooldown before resend is allowed
  TOKEN_REFRESH_THRESHOLD: 5 * 60 * 1000, // 5 minutes before expiry
} as const

// API configuration
export const API_CONFIG = {
  BASE_URL: process.env.NEXT_PUBLIC_API_URL || 'https://api.ethpass.net',
  VERSION: 'v1',
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
} as const

// UI configuration
export const UI_CONFIG = {
  TOAST_DURATION: 4000,
  DEBOUNCE_DELAY: 300,
  PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  ANIMATION_DURATION: 300,
  MOBILE_BREAKPOINT: 768,
  TABLET_BREAKPOINT: 1024,
  DESKTOP_BREAKPOINT: 1280,
} as const

// Language configuration
export const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'am', name: 'አማርኛ', flag: '🇪🇹' },
  { code: 'om', name: 'Afaan Oromoo', flag: '🇪🇹' },
  { code: 'ti', name: 'ትግርኛ', flag: '🇪🇹' },
] as const

// Security headers
export const SECURITY_HEADERS = {
  'X-UA-Compatible': 'IE=edge',
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options': 'DENY',
  'X-XSS-Protection': '1; mode=block',
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  'Permissions-Policy': 'camera=(), microphone=(), geolocation=()',
  'Strict-Transport-Security': 'max-age=31536000; includeSubDomains',
} as const

// Ethiopian regions
export const ETHIOPIAN_REGIONS = [
  { code: 'AA', name: 'Addis Ababa', type: 'city' },
  { code: 'DD', name: 'Dire Dawa', type: 'city' },
  { code: 'AF', name: 'Afar', type: 'region' },
  { code: 'AM', name: 'Amhara', type: 'region' },
  { code: 'BN', name: 'Benishangul-Gumuz', type: 'region' },
  { code: 'GM', name: 'Gambela', type: 'region' },
  { code: 'HR', name: 'Harari', type: 'region' },
  { code: 'OR', name: 'Oromia', type: 'region' },
  { code: 'SM', name: 'Somali', type: 'region' },
  { code: 'SN', name: 'Southern Nations', type: 'region' },
  { code: 'TG', name: 'Tigray', type: 'region' },
] as const

// Status indicators
export const STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING: 'pending',
  SUSPENDED: 'suspended',
  LOCKED: 'locked',
} as const

// Error codes
export const ERROR_CODES = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  AUTHENTICATION_FAILED: 'AUTHENTICATION_FAILED',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  SERVER_ERROR: 'SERVER_ERROR',
  NOT_FOUND: 'NOT_FOUND',
} as const

// Validation patterns
export const VALIDATION_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE_ETHIOPIAN: /^(0[79]\d{8}|251[79]\d{8})$/,
  PASSWORD_STRONG: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
  OTP: /^\d{6}$/,
  NAME: /^[a-zA-Z\s]{2,50}$/,
} as const

// Performance thresholds
export const PERFORMANCE_THRESHOLDS = {
  FCP: 1200, // First Contentful Paint
  LCP: 2500, // Largest Contentful Paint
  FID: 100, // First Input Delay
  CLS: 0.1, // Cumulative Layout Shift
  TTI: 3500, // Time to Interactive
  TBT: 300, // Total Blocking Time
} as const

// Cache configuration
export const CACHE_CONFIG = {
  DEFAULT_TTL: 5 * 60 * 1000, // 5 minutes
  USER_DATA_TTL: 10 * 60 * 1000, // 10 minutes
  STATIC_DATA_TTL: 60 * 60 * 1000, // 1 hour
  PERMISSIONS_TTL: 30 * 60 * 1000, // 30 minutes
} as const

// Feature flags
export const FEATURE_FLAGS = {
  ENABLE_BIOMETRIC_AUTH: false,
  ENABLE_PUSH_NOTIFICATIONS: false,
  ENABLE_OFFLINE_MODE: false,
  ENABLE_ANALYTICS: true,
  ENABLE_ERROR_REPORTING: true,
  ENABLE_PERFORMANCE_MONITORING: true,
} as const
